import { formatNumber } from '@/utils/number';
import { featureGroups } from '../feature-groups';
import type { FeatureGroup } from '../feature-groups';
import { PlanType } from '../plans';
import { OrdwayBillingPeriod } from './plans';
import { baseOrdwayProductIds, ordwayProductIds } from './products';
import type { ProductName } from './products';

// Shared helpers and domain constants used by both Ordway transforms
// (`transforms/ordway-subscription.ts` and `transforms/ordway-plan.ts`).

/** Charge type on an Ordway product/charge. Ported from the Ember `ordway-subscription` model. */
export const OrdwayChargeType = {
  OneTime: 'One time',
  Recurring: 'Recurring',
  Usage: 'Usage based',
} as const;
export type OrdwayChargeType = (typeof OrdwayChargeType)[keyof typeof OrdwayChargeType];

/** Product type custom field on an Ordway product/charge. */
export const OrdwayProductType = {
  Journeys: 'journeys',
  CDP: 'cdp',
} as const;
export type OrdwayProductType = (typeof OrdwayProductType)[keyof typeof OrdwayProductType];

/** An Ordway product line or charge — anything keyed by an Ordway `product_id`. */
type OrdwayLineItem = { product_id: string };

/** Maps an Ordway billing period to its display name. */
export function ordwayIntervalDisplayName(billingPeriod: string | undefined): string {
  if (billingPeriod == null || billingPeriod === '-') {
    return 'custom';
  }

  switch (billingPeriod) {
    case OrdwayBillingPeriod.Annually:
      return 'annual';
    case OrdwayBillingPeriod.SemiAnnually:
      return 'semi-annual';
    default:
      return billingPeriod.toLowerCase();
  }
}

/** Parses a comma-separated Ordway `feature_groups` custom field into feature groups. */
export function parseOrdwayFeatureGroups(featureGroups: string | undefined): FeatureGroup[] {
  if (featureGroups == null || featureGroups === '') {
    return [];
  }

  // oxlint-disable-next-line typescript/consistent-type-assertions -- the backend's comma list, passed through as before; filtering unknown groups would change behavior
  return featureGroups.split(',') as FeatureGroup[];
}

/** Finds the line item whose `product_id` matches the given Ordway product name. */
export function findOrdwayItemByProductName<T extends OrdwayLineItem>(items: T[], name: ProductName): T | undefined {
  const id = ordwayProductIds()[name];
  return items.find((item) => item.product_id === id);
}

/** Finds the line item that corresponds to a known base Ordway product. */
export function findBaseOrdwayItem<T extends OrdwayLineItem>(items: T[]): T | undefined {
  return items.find((item) => baseOrdwayProductIds().includes(item.product_id));
}

/** Builds the plan comparison highlights (with HTML emphasis) for an Ordway plan/subscription. */
// fallow-ignore-next-line complexity -- ported from Ember billing, complexity is inherent to the domain mapping
export function ordwayHighlights(
  type: PlanType,
  groups: FeatureGroup[],
  includedProfiles: number,
  includedMessages: number,
): string[] {
  // Guard against an empty/unknown feature group (sparse data). Ember relied on
  // base products always carrying a feature group; falling back to an empty config
  // yields the same 'Custom'/'Unlimited' output instead of throwing.
  const featureGroup = featureGroups[groups[0]] ?? {};

  if (type === PlanType.CdpPremium || type === PlanType.CdpStarter) {
    const workspaceLimit = featureGroup.featureWorkspaces?.limit ?? 0;

    return [
      `<strong>Unlimited</strong> monthly API calls`,
      `<strong>Unlimited</strong> sources`,
      `<strong>Unlimited</strong> destinations`,
      `<strong>${workspaceLimit === 0 ? 'Unlimited' : formatNumber(workspaceLimit)}</strong> workspaces`,
    ];
  }

  const result: string[] = [];

  if (includedProfiles === 0) {
    result.push(`<strong>Custom</strong> profile volume`);
  } else {
    result.push(`<strong>${formatNumber(includedProfiles)}</strong> profiles`);
  }

  if (includedMessages === 0) {
    result.push(`<strong>Custom</strong> email volume`);
  } else {
    result.push(`<strong>${formatNumber(includedMessages)}</strong> messages per month`);
  }

  result.push(`<strong>${featureGroup.featureObjectTypes?.limit ?? 'Custom'}</strong> object types`);

  return result;
}
